/**
 * The handle rules the API applies, mirrored so the form can say what is wrong before anything is sent (the API stays the judge:
 * reserved names, offensive words and "taken" are only known there). Kept in step with social/handles.py (Bez, 2026-10-04):
 * authority words are refused alone or inside the name, through the same lookalike folding; brand words alone stay allowed.
 */
export const SHAPE = /^[A-Za-z][A-Za-z0-9._]{2,14}$/
export const AUTHORITY_PARTS = ['support', 'admin', 'official', 'moderator', 'staff', 'helpdesk'] as const
const MOD_WORDS = ['mod', 'mods', 'modz']
const BRAND_PARTS = ['cadinu', 'cbon']
const LEET: Record<string, string> = { '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', '8': 'b', '9': 'g', $: 's', '@': 'a' }

const leet = (s: string) => s.replace(/[01345789$@]/g, (c) => LEET[c] ?? c)
export const fold = (h: string) => leet(h.toLowerCase()).replace(/[._]/g, '')
const shapeFold = (s: string) => s.replace(/rn/g, 'm').replace(/vv/g, 'w').replace(/l/g, 'i')
const vowel = (c: string) => 'aeiouy'.includes(c)

/** The authority word a handle contains, or null (same answer as handles.authority_word on the API). */
export function authorityWord(handle: string): string | null {
  const folded = fold(handle)
  const shaped = shapeFold(folded)
  for (const part of AUTHORITY_PARTS) if (folded.includes(part) || shaped.includes(shapeFold(part))) return part
  const segments = handle.toLowerCase().split(/[._]+/).filter(Boolean).map(leet)
  if (segments.some((s) => MOD_WORDS.includes(s))) return 'mod'
  for (const s of [...segments, folded]) {
    if (MOD_WORDS.some((m) => s.endsWith(m))) return 'mod'
    if (s.startsWith('mod') && s.length > 3 && !vowel(s[3])) return 'mod'
  }
  if (BRAND_PARTS.some((b) => folded.includes(`${b}mod`) || folded.includes(`mod${b}`))) return 'mod'
  return null
}

/** What the form says about a handle before it is sent; null when it may go to the API. */
export function handleHint(raw: string): string | null {
  const h = raw.trim()
  if (!h) return null
  if (!SHAPE.test(h) || h.includes('..') || h.endsWith('.') || h.includes('__')) return '3 to 15 characters: start with a letter, then letters, digits, "." or "_".'
  const word = authorityWord(h)
  if (word) return `Handles cannot contain "${word}", even inside a longer name: support, admin, official, mod, staff and helpdesk are kept so nobody can pose as CADINU. Brand words like "cadinu" are fine.`
  return null
}
